import React, { useState, useMemo } from "react"
import mapImg from "../images/map.webp"
import data from '../data/data.json'

const PAGE_SIZE = 5

export default function Research() {
  const [tab, setTab] = useState("articles")
  const [query, setQuery] = useState("")
  const [page, setPage] = useState(0)

  const articles = data.research.articles
  const chapters = data.research.book_chapters

  const filtered = useMemo(() => {
    const q = query.toLowerCase()
    if (!q) return articles
    return articles.filter(a =>
      a.title.toLowerCase().includes(q) ||
      a.journal.toLowerCase().includes(q) ||
      a.authors.toLowerCase().includes(q) ||
      String(a.year).includes(q)
    )
  }, [query, articles])

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const pageItems = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)

  function handleSearch(e) {
    setQuery(e.target.value)
    setPage(0)
  }

  return (
    <div style={wrap} id="research">
      <div style={inner} className="ag-inner">
        <h2 style={titleStyle}>Research</h2>
        <div style={layout} className="research-layout">

          {/* Left column */}
          <div style={leftCol}>
            <div style={tabRow}>
              {[["articles", "Journal Articles"], ["chapters", "Book Chapters"]].map(([key, label]) => (
                <button
                  key={key}
                  style={{ ...tabBtn, ...(tab === key ? tabActive : {}) }}
                  onClick={() => { setTab(key); setQuery(""); setPage(0) }}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === "articles" && (
              <>
                <div style={searchWrap}>
                  <input
                    type="text"
                    placeholder="Search articles…"
                    value={query}
                    onChange={handleSearch}
                    style={searchInput}
                  />
                </div>

                <div>
                  {pageItems.length === 0 && (
                    <p style={noResults}>No articles match "{query}"</p>
                  )}
                  {pageItems.map(item => (
                    <div key={item.id} style={card}>
                      <div style={cardLeft}>
                        <div style={cardMeta}>
                          <span style={cardYear}>{item.year}</span>
                          <span style={cardJournal}>{item.journal}</span>
                        </div>
                        <p style={cardTitle}>{item.title}</p>
                        <span style={cardAuthors}>{item.authors}</span>
                      </div>
                      <div style={cardActions}>
                        {item.pdf && item.pdf !== "none" && (
                          <a style={resBtn} href={`/files/${item.pdf}.pdf?v=2`} target="_blank" rel="noreferrer">PDF</a>
                        )}
                        {item.link && (
                          <a style={resBtn} href={item.link} target="_blank" rel="noreferrer">Link</a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div style={pagination}>
                    <button
                      style={{ ...pageBtn, ...(page === 0 ? pageBtnDisabled : {}) }}
                      onClick={() => setPage(p => p - 1)}
                      disabled={page === 0}
                    >
                      ← Prev
                    </button>
                    <span style={pageIndicator}>{page + 1} / {totalPages}</span>
                    <button
                      style={{ ...pageBtn, ...(page === totalPages - 1 ? pageBtnDisabled : {}) }}
                      onClick={() => setPage(p => p + 1)}
                      disabled={page === totalPages - 1}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}

            {tab === "chapters" && (
              <div style={chaptersGrid}>
                {chapters.map(item => (
                  <div key={item.id} style={chapterCard}>
                    <p style={chapterYear}>{item.year}</p>
                    <p style={chapterBook}>{item.book}</p>
                    <p style={chapterTitle}>{item.title}</p>
                    <div style={chapterLinks}>
                      {item.pdf && item.pdf !== "none" && (
                        <a style={resBtn} href={`/files/${item.pdf}.pdf?v=2`} target="_blank" rel="noreferrer">PDF</a>
                      )}
                      {item.link && (
                        <a style={resBtn} href={item.link} target="_blank" rel="noreferrer">Publisher</a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right column — background image */}
          <div style={rightCol} className="research-right-col">
            <div style={imgOverlay} className="research-img-overlay" />
            <a
              href="https://scholar.google.com/citations?user=QxAZtYYAAAAJ&hl=en"
              target="_blank"
              rel="noreferrer"
              style={scholarBtn}
              className="research-scholar-btn"
            >
              View on Google Scholar<span className="scholar-arrow"> →</span>
            </a>
          </div>

        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .research-layout { grid-template-columns: 1fr !important; }
          .research-right-col { height: 240px !important; }
        }
        @media (max-width: 600px) {
          .research-right-col {
            background-image: none !important;
            height: auto !important;
            padding: 0 !important;
            display: block !important;
            margin-top: 8px !important;
          }
          .research-img-overlay { display: none !important; }
          .research-scholar-btn {
            color: var(--cobalt) !important;
            border-color: var(--cobalt) !important;
            background: transparent !important;
            backdrop-filter: none !important;
            padding: 11px 14px !important;
            font-size: 10px !important;
            letter-spacing: 0.14em !important;
            white-space: nowrap !important;
            display: block !important;
            text-align: center !important;
          }
          .scholar-arrow { display: none !important; }
        }
      `}</style>
    </div>
  )
}

const wrap = {
  background: "var(--cobalt-light)",
  padding: "96px 0",
}

const inner = {
  maxWidth: "1320px",
  margin: "0 auto",
  padding: "0 40px",
}

const titleStyle = {
  fontFamily: '"Avant Garde", sans-serif',
  fontWeight: 300,
  fontSize: "32px",
  letterSpacing: "0.04em",
  color: "var(--cobalt)",
  marginBottom: "48px",
}

const layout = {
  display: "grid",
  gridTemplateColumns: "1fr 380px",
  gap: "64px",
  alignItems: "stretch",
}

const leftCol = {
  minWidth: 0,
}

const rightCol = {
  position: "relative",
  backgroundImage: `url(${mapImg})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  padding: "32px",
}

const imgOverlay = {
  position: "absolute",
  inset: 0,
  background: "linear-gradient(to top, rgba(28,54,116,0.75) 0%, rgba(28,54,116,0.15) 60%)",
}

const scholarBtn = {
  position: "relative",
  zIndex: 1,
  fontFamily: '"Avant Garde", sans-serif',
  fontSize: "11px",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "white",
  border: "1px solid rgba(255,255,255,0.7)",
  padding: "13px 28px",
  textDecoration: "none",
  display: "inline-block",
  backdropFilter: "blur(4px)",
  background: "rgba(28,54,116,0.3)",
}

const tabRow = {
  display: "flex",
  gap: 0,
  marginBottom: "32px",
  borderBottom: "2px solid var(--cream-mid)",
}

const tabBtn = {
  fontFamily: '"Avant Garde", sans-serif',
  fontSize: "11px",
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--ink-soft)",
  padding: "12px 24px",
  cursor: "pointer",
  border: "none",
  background: "none",
  borderBottom: "2px solid transparent",
  marginBottom: "-2px",
  transition: "all 0.2s",
}

const tabActive = {
  color: "var(--cobalt)",
  borderBottomColor: "var(--cobalt)",
}

const searchWrap = {
  marginBottom: "24px",
}

const searchInput = {
  width: "100%",
  padding: "10px 16px",
  fontFamily: '"Avant Garde", sans-serif',
  fontSize: "13px",
  letterSpacing: "0.04em",
  border: "1px solid var(--cream-mid)",
  background: "white",
  color: "var(--ink)",
  outline: "none",
  boxSizing: "border-box",
}

const noResults = {
  fontFamily: '"Avant Garde", sans-serif',
  fontSize: "13px",
  color: "var(--ink-soft)",
  padding: "24px 0",
}

const card = {
  display: "grid",
  gridTemplateColumns: "1fr auto",
  gap: "8px 24px",
  alignItems: "start",
  padding: "14px 0",
  borderTop: "1px solid var(--cream-mid)",
}

const cardLeft = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
}

const cardMeta = {
  display: "flex",
  gap: "10px",
  alignItems: "center",
  flexWrap: "wrap",
}

const cardYear = {
  fontFamily: '"Avant Garde", sans-serif',
  fontSize: "10px",
  letterSpacing: "0.12em",
  color: "var(--gold)",
}

const cardTitle = {
  fontSize: "15px",
  lineHeight: 1.4,
  color: "var(--ink)",
  margin: "2px 0 0",
}

const cardJournal = {
  fontFamily: '"Avant Garde", sans-serif',
  fontSize: "10px",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--cobalt-mid)",
}

const cardAuthors = {
  fontSize: "12px",
  color: "var(--ink-soft)",
  fontStyle: "italic",
  lineHeight: 1.3,
}

const cardActions = {
  display: "flex",
  gap: "8px",
  alignItems: "flex-start",
  paddingTop: "4px",
  flexShrink: 0,
}

const resBtn = {
  fontFamily: '"Avant Garde", sans-serif',
  fontSize: "9px",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--cobalt-mid)",
  border: "1px solid currentColor",
  padding: "5px 12px",
  textDecoration: "none",
  whiteSpace: "nowrap",
  display: "inline-block",
}

const pagination = {
  display: "flex",
  alignItems: "center",
  gap: "20px",
  marginTop: "32px",
  paddingTop: "24px",
  borderTop: "1px solid var(--cream-mid)",
}

const pageBtn = {
  fontFamily: '"Avant Garde", sans-serif',
  fontSize: "11px",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--cobalt)",
  border: "none",
  background: "none",
  cursor: "pointer",
  padding: 0,
}

const pageBtnDisabled = {
  color: "var(--cream-mid)",
  cursor: "default",
}

const pageIndicator = {
  fontFamily: '"Avant Garde", sans-serif',
  fontSize: "11px",
  color: "var(--ink-soft)",
  letterSpacing: "0.08em",
}

const chaptersGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "24px",
  marginTop: "8px",
}

const chapterCard = {
  background: "white",
  padding: "28px 24px",
  borderTop: "3px solid var(--cobalt)",
}

const chapterYear = {
  fontFamily: '"Avant Garde", sans-serif',
  fontSize: "11px",
  color: "var(--gold)",
  letterSpacing: "0.08em",
  marginBottom: "4px",
}

const chapterBook = {
  fontFamily: '"Avant Garde", sans-serif',
  fontSize: "10px",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--cobalt-mid)",
  marginBottom: "4px",
}

const chapterTitle = {
  fontSize: "17px",
  lineHeight: 1.5,
  color: "var(--ink)",
  marginBottom: "16px",
}

const chapterLinks = {
  display: "flex",
  gap: "8px",
}
